"use client"

import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/controls/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/layout/card"
import { Instagram, ExternalLink, WifiOff, TrendingUp, Heart, Users, BarChart3, ClipboardCheck, UserCheck, Building2, Link2 } from "lucide-react"
import { useToast } from "@/lib/hooks/use-toast"
import apiClient from "@/lib/api/api-client"
import { isCapacitorNative } from "@/lib/capacitor"
import {
    currentAppReturnPath,
    OAUTH_RETURN_EVENT,
    openExternalAuth,
    type OAuthReturnDetail,
} from "@/lib/capacitor/oauth-browser"

interface InstagramConnectButtonProps {
    playerId: string
    onSuccess?: () => void
    className?: string
    children?: React.ReactNode
    onError?: () => void
    showIcon?: boolean
}

export function InstagramConnectButton({ playerId, onSuccess, className, children, onError, showIcon = true }: InstagramConnectButtonProps) {
    const [isConnecting, setIsConnecting] = useState(false)
    const { toast } = useToast()
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const finishingRef = useRef(false)
    const onErrorRef = useRef(onError)
    onErrorRef.current = onError

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current)
            pollRef.current = null
        }
    }

    const finishConnected = async () => {
        if (finishingRef.current) return
        finishingRef.current = true
        stopPolling()
        toast({ title: "Instagram connected", description: "Starting initial sync..." })
        onSuccess?.()
        setIsConnecting(false)
        finishingRef.current = false
    }

    const startPolling = () => {
        stopPolling()
        let attempts = 0
        const maxAttempts = isCapacitorNative() ? 80 : 10
        pollRef.current = setInterval(async () => {
            attempts += 1
            try {
                const ig = await apiClient.instagram.getByPlayer(playerId)
                if (ig?.isConnected) {
                    await finishConnected()
                    return
                }
            } catch {
                // keep polling
            }
            if (attempts >= maxAttempts) {
                stopPolling()
                setIsConnecting(false)
                onErrorRef.current?.()
            }
        }, 2000)
    }

    useEffect(() => {
        const onOAuthReturn = (event: Event) => {
            const detail = (event as CustomEvent<OAuthReturnDetail>).detail
            if (detail?.provider !== "instagram") return
            if (detail.playerId && detail.playerId !== playerId) return
            if (detail.error) {
                stopPolling()
                setIsConnecting(false)
                toast({
                    title: "Instagram connection failed",
                    description: "Authorization was cancelled or did not complete. Try again.",
                    variant: "destructive",
                })
                onErrorRef.current?.()
                return
            }
            void finishConnected()
        }

        window.addEventListener(OAUTH_RETURN_EVENT, onOAuthReturn)
        return () => {
            window.removeEventListener(OAUTH_RETURN_EVENT, onOAuthReturn)
            stopPolling()
        }
    }, [playerId])

    const handleConnect = async () => {
        try {
            setIsConnecting(true)
            finishingRef.current = false
            const native = isCapacitorNative()
            const res = await apiClient.instagram.connect({
                playerId,
                ...(native
                    ? { client: "native", returnPath: currentAppReturnPath() }
                    : {}),
            })
            const url = res.authUrl

            if (url) {
                await openExternalAuth(url)
                toast({
                    title: "Complete Instagram authorization",
                    description: native
                        ? "Sign in if asked. You will return here automatically when it finishes."
                        : "A new window was opened. After authorizing, return here.",
                })
                startPolling()
            } else {
                setIsConnecting(false)
            }
        } catch (e: any) {
            setIsConnecting(false)
            toast({
                title: "Failed to start connection",
                description: e?.message || "Unexpected error",
                variant: "destructive"
            })
            onErrorRef.current?.()
        }
    }

    return (
        <Button onClick={handleConnect} disabled={isConnecting} className={className}>
            {isConnecting ? (
                <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Connecting...
                </>
            ) : (
                <>
                    {showIcon ? <ExternalLink className="mr-2 h-4 w-4" /> : null}
                    {children || "Connect Instagram"}
                </>
            )}
        </Button>
    )
}

interface InstagramNoConnectionProps {
    playerId: string
    onSuccess?: () => void
}

export function InstagramNoConnection({ playerId, onSuccess }: InstagramNoConnectionProps) {
    return (
        <div className="space-y-6">
            <div className="text-center py-8">
                <div className="relative mx-auto mb-4 w-20 h-20">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-2xl rotate-6 opacity-20" />
                    <div className="relative flex items-center justify-center w-full h-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-2xl">
                        <Instagram className="h-10 w-10 text-white" />
                    </div>
                </div>
                <h2 className="text-2xl font-bold text-foreground mb-2">No Instagram connection</h2>
                <p className="text-muted-foreground mb-6">Connect Instagram to view social media insights and analytics</p>
                <InstagramConnectButton
                    playerId={playerId}
                    onSuccess={onSuccess}
                    className="text-lg px-6 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 hover:from-purple-600 hover:via-pink-600 hover:to-orange-500"
                >
                    Connect Instagram
                </InstagramConnectButton>
            </div>

            <Card className="shadow-lg">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <ClipboardCheck className="h-5 w-5" />
                        What you need to have before connecting
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-pink-100 p-2">
                                <Instagram className="h-4 w-4 text-pink-600" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Instagram professional account</p>
                                <p className="text-sm text-muted-foreground">Creator or Business — personal profiles cannot connect</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-blue-100 p-2">
                                <UserCheck className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Facebook account</p>
                                <p className="text-sm text-muted-foreground">You sign in with Facebook, not Instagram</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-purple-100 p-2">
                                <Building2 className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Facebook Page you admin</p>
                                <p className="text-sm text-muted-foreground">That Facebook account must administer the Page</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-orange-100 p-2">
                                <Link2 className="h-4 w-4 text-orange-600" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Instagram linked to the Page</p>
                                <p className="text-sm text-muted-foreground">Link them in Meta Business Suite or Instagram settings</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <Card className="shadow-lg">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        What you'll get after connecting
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-pink-100 p-2">
                                <Users className="h-4 w-4 text-pink-600" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Follower Analytics</p>
                                <p className="text-sm text-muted-foreground">Track follower growth and demographics</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-purple-100 p-2">
                                <Heart className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Engagement Metrics</p>
                                <p className="text-sm text-muted-foreground">Likes, comments, and interactions</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-blue-100 p-2">
                                <TrendingUp className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Reach & Impressions</p>
                                <p className="text-sm text-muted-foreground">See how far your content travels</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="rounded-full bg-orange-100 p-2">
                                <BarChart3 className="h-4 w-4 text-orange-600" />
                            </div>
                            <div>
                                <p className="font-medium text-foreground">Weekly Reports</p>
                                <p className="text-sm text-muted-foreground">Performance trends and insights</p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}
